<template>
  <div class="auth-settings">

    <div class="section section-gate">
      <div class="gate-panel" :class="{ 'gate-panel--on': loginRequired }">
        <div class="gate-panel-main">
          <h3 class="gate-title">Login Required</h3>
          <p class="gate-desc">
            When on, visitors must sign in before they can view this site.
          </p>
        </div>
        <div class="gate-panel-control" aria-label="Login required">
          <span class="toggle-status-label" :class="{ on: loginRequired }">
            {{ loginRequired ? 'On' : 'Off' }}
          </span>
          <div
            class="toggle-switch"
            role="switch"
            :class="{ on: loginRequired }"
            :aria-checked="loginRequired"
            tabindex="0"
            @click="updateField('loginRequired', !loginRequired)"
            @keydown.enter.prevent="updateField('loginRequired', !loginRequired)"
            @keydown.space.prevent="updateField('loginRequired', !loginRequired)"
          >
            <div class="toggle-knob"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Section 2: Login Endpoint ─────────────────────── -->
    <div class="section">
      <div class="section-header">
        <div>
          <div class="section-title">Auth API URL</div>
          <p class="section-desc" v-if="authUrlMode === 'builder'">Using the builder's built-in login — no setup needed. Credentials are verified against your builder user accounts.</p>
          <p class="section-desc" v-else>Your custom endpoint will receive the login form data and must return a token in the response.</p>
        </div>
      </div>

      <div class="mode-tabs" role="tablist">
        <button
          type="button"
          class="mode-tab"
          role="tab"
          :aria-selected="authUrlMode === 'builder'"
          :class="{ active: authUrlMode === 'builder' }"
          @click="setAuthUrlMode('builder')"
        >
          <span>Builder login</span>
          <span class="mode-tab-badge">Suggested</span>
        </button>
        <button
          type="button"
          class="mode-tab"
          role="tab"
          :aria-selected="authUrlMode === 'custom'"
          :class="{ active: authUrlMode === 'custom' }"
          @click="setAuthUrlMode('custom')"
        >
          Custom URL
        </button>
      </div>

      <div v-if="authUrlMode === 'builder'" class="info-box">
        <span class="info-box-check" aria-hidden="true">
          <font-awesome-icon icon="check" />
        </span>
        <div class="info-box-body">
          <p class="info-box-lead">Built-in authentication is active. No external API setup is required.</p>
          <div class="info-box-url">
            <span class="info-box-method">POST</span>
            <code>{{ builderLoginUrl }}</code>
          </div>
        </div>
      </div>

      <div v-else class="endpoint-row">
        <div class="custom-select" :class="{ open: openDropdown === 'main' }">
          <div class="custom-select-trigger" @click="openDropdown = openDropdown === 'main' ? null : 'main'">
            <span class="custom-select-value" :class="`method-${(authAPIMethod||'GET').toLowerCase()}`">{{ authAPIMethod || 'GET' }}</span>
            <span class="custom-select-arrow">▾</span>
          </div>
          <div class="custom-select-menu" v-if="openDropdown === 'main'">
            <div v-for="m in ['GET','POST','PUT']" :key="m"
              class="custom-select-option" :class="[`method-${m.toLowerCase()}`, { selected: authAPIMethod === m }]"
              @click="updateField('authAPIMethod', m); openDropdown = null"
            >
              {{ m }}
              <span v-if="authAPIMethod === m" class="option-check">✓</span>
            </div>
          </div>
        </div>
        <input
          class="url-input"
          type="text"
          placeholder="https://your-api.com/auth/login"
          :value="authUrl"
          @change="updateField('authUrl', $event.target.value)"
        />
      </div>

      <div v-if="authUrlMode === 'custom'" class="field-row mt-2">
        <label class="field-label">
          Token path in response
          <span class="field-hint">JSON path or template for the token, e.g. <code>&#123;&#123;token&#125;&#125;</code> or <code>&#123;&#123;data.token&#125;&#125;</code>.</span>
        </label>
        <input
          class="url-input"
          type="text"
          placeholder="{{token}}"
          :value="authResponse"
          @change="updateField('authResponse', $event.target.value)"
        />
      </div>
    </div>

    <!-- ── Section 3: Extra Auth Sources ─────────────────── -->
    <div class="section">
      <div class="section-header">
        <div>
          <div class="section-title">
            Extra API auth sources
            <span v-if="(extraAuths || []).length" class="badge-count">{{ (extraAuths || []).length }}</span>
          </div>
          <p class="section-desc">Use <code class="inline-code">ggAuth(your key)</code> as Authorization header on remote source data.</p>
        </div>
      </div>

      <div
        class="auth-source-card"
        v-for="(authItem, aIdx) of extraAuths"
        :key="aIdx"
      >
        <div class="auth-source-head">
          <label class="field-compact auth-source-key-field-wrap">
            <span class="field-compact-label">Key</span>
            <div class="auth-source-key-tools">
              <input
                type="text"
                :value="authItem.key"
                placeholder="e.g. myApi"
                class="auth-source-key-field"
                @change="updateExtraAuth(aIdx, 'key', $event.target.value)"
              />
              <div class="auth-source-actions" role="group" aria-label="Source actions">
                <button type="button" class="icon-btn ghost" title="Headers and body" @click="toggleExtraAuthConfig(aIdx)">
                  <font-awesome-icon icon="cog" />
                </button>
                <button type="button" class="icon-btn ghost danger-icon" title="Remove source" @click="removeExtraAuth(aIdx)">
                  <font-awesome-icon icon="minus" />
                </button>
              </div>
            </div>
          </label>
        </div>

        <div class="auth-source-url-row">
          <div class="field-compact field-compact-grow">
            <span class="field-compact-label">Request</span>
            <div class="endpoint-row-inner">
              <div class="custom-select" :class="{ open: openDropdown === `src-${aIdx}` }">
                <div class="custom-select-trigger" @click.stop="openDropdown = openDropdown === `src-${aIdx}` ? null : `src-${aIdx}`">
                  <span class="custom-select-value" :class="`method-${(authItem.method||'GET').toLowerCase()}`">{{ authItem.method || 'GET' }}</span>
                  <span class="custom-select-arrow">▾</span>
                </div>
                <div class="custom-select-menu" v-if="openDropdown === `src-${aIdx}`">
                  <div v-for="m in ['GET','POST','PUT']" :key="m"
                    class="custom-select-option" :class="[`method-${m.toLowerCase()}`, { selected: authItem.method === m }]"
                    @click="updateExtraAuth(aIdx, 'method', m); openDropdown = null"
                  >
                    {{ m }}
                    <span v-if="authItem.method === m" class="option-check">✓</span>
                  </div>
                </div>
              </div>
              <input
                class="url-input flex-1"
                type="text"
                placeholder="https://your-api.com/auth/token"
                :defaultValue="authItem.url"
                @change="updateExtraAuth(aIdx, 'url', $event.target.value)"
              />
            </div>
          </div>
        </div>

        <div class="auth-source-meta">
          <label class="server-option">
            <input
              type="checkbox"
              class="server-option-input"
              :checked="!!authItem.serverOnly"
              @change="updateExtraAuth(aIdx, 'serverOnly', !!$event.target.checked)"
            />
            <span class="server-option-copy">
              <span class="server-option-title">Fetch token on the server</span>
              <span class="server-option-hint">No browser prompt — the builder retrieves the token for you.</span>
            </span>
          </label>
        </div>

        <!-- expanded config panel -->
        <div v-if="extraAuthEditingConfigIdx === aIdx" class="auth-config-panel">
          <div class="config-tabs">
            <button
              type="button"
              class="config-tab"
              :class="{ active: extraAuthEditingKey === 'headers' }"
              @click="extraAuthEditingKey = 'headers'; newExtraAuthConf = { key: '', value: '' }"
            >
              Headers <span class="config-tab-count">{{ (authItem['headers'] || []).length }}</span>
            </button>
            <button
              type="button"
              class="config-tab"
              :class="{ active: extraAuthEditingKey === 'body' }"
              @click="extraAuthEditingKey = 'body'; newExtraAuthConf = { key: '', value: '' }"
            >
              Body <span class="config-tab-count">{{ (authItem['body'] || []).length }}</span>
            </button>
          </div>

          <div class="kv-list">
            <div class="kv-header">
              <span>Key</span>
              <span>Value</span>
              <span></span>
            </div>
            <div
              class="kv-row"
              v-for="(item, itemIdx) of (authItem[extraAuthEditingKey] || [])"
              :key="`${extraAuthEditingKey}-${itemIdx}`"
            >
              <input
                type="text"
                :value="item.key"
                placeholder="Key"
                class="kv-input"
                @change="updateExtraAuthConf(itemIdx, 'key', $event.target.value)"
              />
              <input
                type="text"
                :value="item.value"
                placeholder="Value"
                class="kv-input"
                @change="updateExtraAuthConf(itemIdx, 'value', $event.target.value)"
              />
              <button type="button" class="icon-btn danger small" @click="removeExtraAuthConf(itemIdx)">
                <font-awesome-icon icon="minus" />
              </button>
            </div>
            <!-- new row -->
            <div class="kv-row new-row">
              <input
                type="text"
                v-model="newExtraAuthConf.key"
                placeholder="New key..."
                class="kv-input"
              />
              <input
                type="text"
                v-model="newExtraAuthConf.value"
                placeholder="New value..."
                class="kv-input"
                @change="addExtraAuthConf()"
              />
              <button type="button" class="icon-btn add small" @click="addExtraAuthConf()">
                <font-awesome-icon icon="plus" />
              </button>
            </div>
          </div>

          <div class="response-row">
            <label class="field-label">
              Token path in response
              <span class="field-hint">Where is the token in the response? e.g. <code>access_token</code></span>
            </label>
            <input
              type="text"
              :defaultValue="authItem['response']"
              placeholder="e.g. access_token or data.token"
              class="url-input"
              @change="updateExtraAuth(aIdx, 'response', $event.target.value)"
            />
          </div>
        </div>
      </div>

      <button type="button" class="add-source-btn" @click="addExtraAuth">
        <font-awesome-icon icon="plus" />
        Add auth source
      </button>
    </div>

  </div>
</template>

<script>
import { api_host } from "@/constants";

const BUILDER_LOGIN_URL = `${api_host}/auth/login`;

export default {
  name: "SettingsAuth",
  emits: ["update"],
  props: {
    loginRequired: Boolean,
    authUrl: String,
    authAPIMethod: String,
    authResponse: String,
    extraAuths: Array,
  },
  data() {
    return {
      extraAuthEditingConfigIdx: -1,
      extraAuthEditingKey: "headers",
      authUrlMode: !this.authUrl || this.authUrl === BUILDER_LOGIN_URL ? "builder" : "custom",
      newExtraAuthConf: { key: '', value: '' },
      builderLoginUrl: BUILDER_LOGIN_URL,
      openDropdown: null,
    }
  },
  mounted() {
    document.addEventListener('click', this.closeDropdown);
    if (this.authUrlMode === 'builder' && !this.authUrl) {
      this.$emit('update', { authUrl: BUILDER_LOGIN_URL, authAPIMethod: 'POST' });
    }
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeDropdown);
  },
  methods: {
    closeDropdown(e) {
      if (!e.target.closest('.custom-select')) this.openDropdown = null;
    },
    setAuthUrlMode(mode) {
      this.authUrlMode = mode;
      if (mode === "builder") {
        this.$emit("update", { authUrl: BUILDER_LOGIN_URL, authAPIMethod: "POST" });
      }
    },
    updateField(field, value) {
      this.$emit("update", { [field]: value });
    },
    updateExtraAuth(idx, field, value) {
      const clone = JSON.parse(JSON.stringify(this.extraAuths || []));
      clone[idx][field] = value;
      this.$emit("update", { extraAuths: clone });
    },
    removeExtraAuth(idx) {
      const clone = JSON.parse(JSON.stringify(this.extraAuths || []));
      clone.splice(idx, 1);
      this.$emit("update", { extraAuths: clone });
    },
    addExtraAuth() {
      this.$emit("update", {
        extraAuths: (this.extraAuths || []).concat({ key: "", method: "POST", url: "" }),
      });
    },
    toggleExtraAuthConfig(idx) {
      this.extraAuthEditingConfigIdx = this.extraAuthEditingConfigIdx === idx ? -1 : idx;
      this.extraAuthEditingKey = "headers";
      this.newExtraAuthConf = { key: '', value: '' };
    },
    updateExtraAuthConf(itemIdx, field, value) {
      const clone = JSON.parse(JSON.stringify(this.extraAuths || []));
      clone[this.extraAuthEditingConfigIdx][this.extraAuthEditingKey][itemIdx][field] = value;
      this.$emit("update", { extraAuths: clone });
    },
    addExtraAuthConf() {
      if (!this.newExtraAuthConf.key && !this.newExtraAuthConf.value) return;
      const clone = JSON.parse(JSON.stringify(this.extraAuths || []));
      if (!clone[this.extraAuthEditingConfigIdx][this.extraAuthEditingKey]) {
        clone[this.extraAuthEditingConfigIdx][this.extraAuthEditingKey] = [];
      }
      clone[this.extraAuthEditingConfigIdx][this.extraAuthEditingKey].push({
        key: this.newExtraAuthConf.key,
        value: this.newExtraAuthConf.value,
      });
      this.newExtraAuthConf = { key: '', value: '' };
      this.$emit("update", { extraAuths: clone });
    },
    removeExtraAuthConf(itemIdx) {
      const clone = JSON.parse(JSON.stringify(this.extraAuths || []));
      clone[this.extraAuthEditingConfigIdx][this.extraAuthEditingKey].splice(itemIdx, 1);
      this.$emit("update", { extraAuths: clone });
    },
  },
}
</script>

<style lang="scss" scoped>
$blue: #00aeef;
$blue-soft: rgba(0, 174, 239, 0.12);
$blue-light: #38bdf8;
$blue-dark: #0077aa;
$border: #e8eaee;
$text: #111827;
$text-muted: #5c6370;
$text-faint: #868b94;

.auth-settings {
  box-sizing: border-box;
  width: 100%;
  max-width: 72rem;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-direction: column;
  gap: 36px;
  padding: 4px 0 16px;
}

.auth-settings > .section {
  width: 100%;
}

// ── Sections ──────────────────────────────────────────
.section {
  display: flex;
  flex-direction: column;
  gap: 18px;

  &-header {
    display: flex;
    align-items: flex-start;
    gap: 0;
    padding-bottom: 14px;
    border-bottom: 1px solid $border;
  }

  &-title {
    font-size: 14px;
    font-weight: 600;
    color: $text;
    letter-spacing: -0.01em;
    display: flex;
    align-items: center;
    gap: 10px;
    line-height: 1.35;
  }

  &-desc {
    font-size: 13px;
    color: $text-muted;
    margin: 8px 0 0;
    line-height: 1.55;
  }
}

.inline-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 11.5px;
  font-weight: 500;
  background: #f1f3f7;
  color: #3d4554;
  padding: 0.1em 0.4em;
  border-radius: 4px;
}

// ── Login gate (panel + inline toggle) ──────────────
.section-gate {
  gap: 0;
}

.gate-panel {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 20px;
  padding: 14px 18px;
  border: 1px solid $border;
  border-radius: 10px;
  background: #fafbfc;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;

  &--on {
    border-color: #b8e8f7;
    background: #f5fbfd;
    box-shadow: 0 1px 3px rgba(0, 174, 239, 0.06);
  }

  &:hover {
    border-color: #dde1e8;
  }

  &--on:hover {
    border-color: #9ddcef;
  }
}

.gate-panel-main {
  flex: 1 1 12rem;
  min-width: 0;
}

.gate-panel-control {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;

  .toggle-status-label {
    line-height: 22px;
    min-height: 22px;
    display: inline-flex;
    align-items: center;
  }
}

.gate-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: $text;
  letter-spacing: -0.01em;
  line-height: 1.35;
}

.gate-desc {
  margin: 6px 0 0;
  font-size: 12px;
  color: $text-muted;
  line-height: 1.45;
}

.toggle-status-label {
  font-size: 11px;
  font-weight: 600;
  color: $text-faint;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  &.on { color: $blue-dark; }
}

.toggle-switch {
  width: 40px;
  height: 22px;
  background: $border;
  border-radius: 20px;
  position: relative;
  cursor: pointer;
  transition: background 0.22s;
  flex-shrink: 0;

  &.on { background: $blue; }

  .toggle-knob {
    position: absolute;
    top: 3px;
    left: 3px;
    width: 16px;
    height: 16px;
    background: white;
    border-radius: 50%;
    transition: transform 0.22s cubic-bezier(.4,0,.2,1);
    box-shadow: 0 1px 4px rgba(0,0,0,0.18);
  }

  &.on .toggle-knob { transform: translateX(18px); }

  &:focus-visible {
    outline: 2px solid $blue-light;
    outline-offset: 2px;
  }
}

// ── Mode tabs ──────────────────────────────────────────
.mode-tabs {
  display: inline-flex;
  background: #eff1f5;
  border-radius: 10px;
  padding: 4px;
  gap: 2px;
  align-self: flex-start;
  border: 1px solid $border;
}

.mode-tab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  color: $text-muted;
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease;
  white-space: nowrap;

  &:hover { color: $text; }

  &.active {
    background: white;
    color: $text;
    font-weight: 600;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
    border: none;
  }

  &-badge {
    background: transparent;
    color: $blue-dark;
    font-size: 10px;
    padding: 2px 6px;
    border-radius: 999px;
    font-weight: 600;
    letter-spacing: 0.02em;
    border: 1px solid #c9ecfa;
    text-transform: none;
    line-height: 1.2;
  }
}

// ── Info box ───────────────────────────────────────────
.info-box {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 18px;
  background: white;
  border: 1px solid $border;
  border-radius: 12px;
  border-left: 3px solid $blue-light;
  font-size: 13px;
  color: $text-muted;
  line-height: 1.55;
}

.info-box-check {
  flex-shrink: 0;
  margin-top: 2px;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border-radius: 999px;
  color: white;
  background: $blue;
  font-size: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 0.65rem !important;
    height: 0.65rem !important;
  }
}

.info-box-body {
  flex: 1;
  min-width: 0;
}

.info-box-lead {
  margin: 0;
  color: #2b3039;
}

.info-box-url {
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 12px;
  background: #f5f8fb;
  border-radius: 8px;
  border: 1px solid #e2e8ef;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;

  code {
    font-size: 12px;
    color: #333c48;
    font-family: inherit;
    word-break: break-all;
  }
}

.info-box-method {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: white;
  background: #64748b;
  padding: 3px 7px;
  border-radius: 4px;
  flex-shrink: 0;
}

// ── Endpoint row ───────────────────────────────────────
.endpoint-row {
  display: flex;
  gap: 10px;
  align-items: center;
}

.endpoint-row-inner {
  display: flex;
  gap: 10px;
  align-items: center;
  width: 100%;
  min-width: 0;

  .url-input {
    min-width: 0;
  }
}

// ── Custom select ──────────────────────────────────────
.custom-select {
  position: relative;
  flex-shrink: 0;
  user-select: none;

  &-trigger {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: #fafbfc;
    border: 1px solid $border;
    border-radius: 8px;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
    min-width: 82px;
    font-size: 12px;
    font-weight: 600;
    color: #3d4554;
  }

  &:hover &-trigger,
  &.open &-trigger {
    border-color: #c5cad3;
    box-shadow: 0 0 0 3px $blue-soft;
    background: white;
  }

  &-value {
    flex: 1;
    letter-spacing: 0.04em;
    color: #475569;

    &.method-get { color: #16a34a; }
    &.method-post { color: #0284c7; }
    &.method-put { color: #c2410c; }
  }

  &-arrow {
    font-size: 10px;
    color: #94a3b8;
    transition: transform 0.18s;
    line-height: 1;
  }

  &.open &-arrow { transform: rotate(180deg); }

  &-menu {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    right: 0;
    z-index: 100;
    background: white;
    border: 1px solid $border;
    border-radius: 10px;
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.1);
    overflow: hidden;
    animation: dropIn 0.15s ease;
  }

  &-option {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 14px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.12s;
    letter-spacing: 0.04em;

    &:hover { background: #f8fafc; }
    &.selected { background: rgba(0, 174, 239, 0.06); }
  }
}

@keyframes dropIn {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}

.option-check {
  margin-left: auto;
  font-size: 11px;
  color: #7dd3fc;
}

// ── URL input ──────────────────────────────────────────
.url-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid $border;
  border-radius: 8px;
  font-size: 13px;
  color: $text;
  background: white;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: #a8afb9;
  }

  &:focus {
    outline: none;
    border-color: $blue-light;
    box-shadow: 0 0 0 3px $blue-soft;
  }
}

// ── Field label ────────────────────────────────────────
.field-row {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.mt-2 { margin-top: 8px; }

.field-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.field-hint {
  font-size: 11px;
  font-weight: 400;
  color: $text-faint;

  code {
    background: #f3f4f6;
    padding: 1px 4px;
    border-radius: 3px;
    font-size: 10px;
    color: #374151;
  }
}

// ── Auth source card ───────────────────────────────────
.auth-source-card {
  border: 1px solid $border;
  border-radius: 12px;
  overflow: visible;
  background: white;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);

  &:not(:last-child) {
    margin-bottom: 14px;
  }

  &:hover {
    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06);
  }
}

.auth-source-head {
  padding: 16px 16px 0;
}

.auth-source-key-field-wrap {
  width: 100%;
}

.auth-source-key-tools {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;

  .auth-source-key-field {
    flex: 1 1 10rem;
    min-width: 7rem;
    max-width: 16rem;
    width: auto;
  }

  .auth-source-actions {
    margin-left: 0;
  }
}

.auth-source-url-row {
  padding: 12px 16px 0;
}

.auth-source-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.field-compact {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.field-compact-grow {
  flex: 1;
  width: 100%;
}

.field-compact-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #8b919c;
}

.auth-source-key-field {
  padding: 8px 12px;
  border: 1px solid $border;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: $text;
  background: white;

  &::placeholder {
    color: #a8afb9;
  }

  &:focus {
    outline: none;
    border-color: $blue-light;
    box-shadow: 0 0 0 3px $blue-soft;
  }
}

.auth-source-meta {
  padding: 16px;
  padding-top: 14px;
  margin-top: 12px;
  border-top: 1px solid $border;
}

// ── Server option (checkbox row) ────────────────────────
.server-option {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  margin: 0;

  &-input {
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
    margin-top: 2px;
    accent-color: $blue;
    cursor: pointer;
  }

  &-copy {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &-title {
    font-size: 13px;
    font-weight: 600;
    color: $text;
  }

  &-hint {
    font-size: 12px;
    font-weight: 400;
    color: $text-faint;
    line-height: 1.45;
  }
}

// ── Config panel ───────────────────────────────────────
.auth-config-panel {
  border-top: 1px solid $border;
  padding: 16px;
  margin-top: 14px;
  background: #f8f9fb;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.config-tabs {
  display: inline-flex;
  background: #eef2f7;
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
  align-self: flex-start;
}

.config-tab {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 7px 14px;
  border-radius: 6px;
  border: none;
  font-size: 13px;
  font-weight: 500;
  color: $text-muted;
  cursor: pointer;
  background: transparent;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover { color: $text; }

  &.active {
    background: white;
    color: $text;
    font-weight: 600;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);

    .config-tab-count {
      background: $blue-dark;
      color: white;
    }
  }
}

.config-tab-count {
  background: $border;
  color: #374151;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 20px;
  transition: all 0.15s;
}

// ── KV list ────────────────────────────────────────────
.kv-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: white;
  border: 1px solid $border;
  border-radius: 10px;
  padding: 10px;
}

.kv-header {
  display: grid;
  grid-template-columns: 130px 1fr 28px;
  gap: 8px;
  font-size: 10px;
  font-weight: 800;
  color: $text-faint;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 0 4px 4px;
  border-bottom: 1px solid #f3f4f6;
}

.kv-row {
  display: grid;
  grid-template-columns: 130px 1fr 28px;
  gap: 8px;
  align-items: center;

  &.new-row .kv-input { border-style: dashed; color: $text-muted; }
}

.kv-input {
  padding: 6px 10px;
  border: 1px solid $border;
  border-radius: 7px;
  font-size: 12px;
  color: $text;
  background: #fafbfc;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  &:focus {
    outline: none;
    background: white;
    border-color: $blue-light;
    box-shadow: 0 0 0 3px $blue-soft;
  }
}

// ── Response row ───────────────────────────────────────
.response-row {
  padding-top: 10px;
  border-top: 1px dashed $border;
  display: flex;
  flex-direction: column;
  gap: 7px;
}

// ── Buttons ────────────────────────────────────────────
.icon-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid $border;
  background: white;
  color: $text-muted;
  cursor: pointer;
  font-size: 12px;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
  flex-shrink: 0;

  &:hover {
    border-color: #c5cad3;
    color: $blue-dark;
    background: rgba(0, 174, 239, 0.06);
  }

  &.ghost {
    border: none;
    background: transparent;

    &:hover {
      border: 1px solid $border;
      background: rgba(0, 174, 239, 0.06);
      color: $blue-dark;
    }
  }

  &.danger-icon:hover {
    border-color: #fecaca !important;
    color: #dc2626 !important;
    background: #fef2f2 !important;
  }

  &.danger:hover { border-color: #fecaca; color: #dc2626; background: #fef2f2; }

  &.add:hover { border-color: #7dd3fc; color: $blue-dark; background: rgba(0, 174, 239, 0.06); }

  &.small {
    width: 28px;
    height: 28px;
    font-size: 10px;
    border-radius: 6px;
  }
}

.add-source-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border: none;
  border-radius: 8px;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease;
  align-self: flex-start;

  svg {
    color: inherit;
    opacity: 0.85;
  }

  &:hover {
    color: $blue-dark;
    background: rgba(0, 174, 239, 0.08);
  }
}

.badge-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.35rem;
  padding: 0 6px;
  background: #eef1f5;
  color: #4b5563;
  font-size: 11px;
  font-weight: 700;
  border-radius: 6px;
  line-height: 1;
}
</style>